import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { MetaTags } from '@/components/seo/meta-tags';
import { LazyImage } from '@/components/ui/lazy-image';
import { lazy, Suspense, type ReactNode } from 'react';
import { SevenSigmaDifference } from '@/components/7sigma-difference';
import { FeaturedIn } from '@/components/home-sections/featured-in';
import { Hero } from '@/components/home-sections/home-hero';
import { Shard } from '@/components/art/shard';
import { Reveal, Stagger, Row, Num, Hairline, Lift } from '@/components/motion/reveal';

// Lazy load heavy sections that are below the fold
const ServicesOverview = lazy(() =>
  import('@/components/home-sections/services-overview').then((m) => ({
    default: m.ServicesOverview,
  }))
);
const NewsletterSubscribe = lazy(() => import('@/components/blog/NewsletterSubscribe'));
const IndustriesSection = lazy(() =>
  import('@/components/home-sections/industries-section').then((m) => ({
    default: m.IndustriesSection,
  }))
);
const EngagementFlow = lazy(() =>
  import('@/components/home-sections/engagement-flow').then((m) => ({ default: m.EngagementFlow }))
);
const RecentEngagements = lazy(() =>
  import('@/components/home-sections/recent-engagements').then((m) => ({
    default: m.RecentEngagements,
  }))
);
const LatestBlogPosts = lazy(() =>
  import('@/components/home-sections/latest-blog-posts').then((m) => ({
    default: m.LatestBlogPosts,
  }))
);

const offers: { title: string; body: ReactNode }[] = [
  {
    title: 'When the team hits its ceiling',
    body: (
      <>
        Teams reach their max. 7Sigma steps in to{' '}
        <a
          href="/vibe-coded-app-rescue"
          className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-[#2563eb]"
        >
          rescue vibe coded apps
        </a>
        , fix what breaks, scale what works, carry the architecture to its next level of maturity,
        and hand it to the team that will own it.
      </>
    ),
  },
  {
    title: 'A CTO who still builds',
    body: 'Leader, builder, and operator in one seat: technical leadership, hands-on engineering, and team management.',
  },
  {
    title: 'The first call for non-technical founders',
    body: 'A founding technical advisor and partner for founders who need the right first step before the first hire.',
  },
];

export default function HomePage() {
  return (
    <div className="w-full min-h-screen bg-[#0a0a0b]">
      <MetaTags
        title="7Sigma Partners | Fractional CTO & Senior Technical Advisor"
        description="7Sigma Partners: fractional CTO, senior architecture, and hands-on technical leadership. Orlando, Florida. Remote, nationwide."
      />

      <Hero />

      {/* Featured In (trust bar) */}
      <Reveal>
        <FeaturedIn />
      </Reveal>

      {/* Where 7Sigma comes in */}
      <section className="pt-6 pb-10 md:pt-8 md:pb-20 relative isolate overflow-hidden">
        <Shard side="left" variant="boxes" />
        <div className="max-w-5xl mx-auto px-4">
          <Reveal>
            <h2 className="text-[1.75rem] md:text-5xl font-bold text-white mb-8 md:mb-12 tracking-tight">
              Where 7Sigma comes in
            </h2>
          </Reveal>
          <Hairline />
          <Stagger className="divide-y divide-white/10 border-b border-white/10">
            {offers.map((o, i) => (
              <Row
                key={o.title}
                className="py-5 md:py-8 flex gap-3 md:grid md:grid-cols-[3rem_1fr] md:gap-6"
              >
                <Num className="text-sm md:text-2xl font-semibold text-white/45 md:text-white/35 tabular-nums pt-1 md:pt-0 w-6 md:w-auto flex-shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </Num>
                <div>
                  <h3 className="text-lg md:text-2xl font-semibold text-white tracking-tight">
                    {o.title}
                  </h3>
                  <p className="text-white/65 leading-relaxed mt-2 max-w-2xl">{o.body}</p>
                </div>
              </Row>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Background (summary) + engagements */}
      <section id="background" className="py-12 md:py-24 relative isolate overflow-hidden">
        <Shard side="right" variant="mesh" />
        <div className="max-w-5xl mx-auto px-4">
          <Reveal>
            <h2 className="text-[1.75rem] md:text-5xl font-bold mb-7 md:mb-10 text-white tracking-tight">
              Background
            </h2>
          </Reveal>

          <Reveal className="flex flex-col sm:flex-row items-start gap-6 mb-6" delay={0.1}>
            <a
              href="https://www.linkedin.com/in/robertjchristian/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <LazyImage
                src="/blogs/authors/rob.jpeg"
                alt="Robert Christian"
                className="w-20 h-20 rounded-full object-cover grayscale"
              />
            </a>
            <div>
              <h3 className="text-xl font-semibold text-white">Robert Christian, Principal</h3>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mt-3 max-w-3xl">
                Twenty-five years building platforms, developer ecosystems, and technology
                organizations. As Chief Architect at Liaison Technologies (acquired by OpenText) he
                helped launch ALLOY, a data platform trusted by Merck, HP, Dell, Boeing, and
                Coca-Cola. At OpenText he led a global product initiative for Trading Grid, the
                largest B2B integration network in the world, moving more than $11 trillion a year.
                He has also built for SeaWorld, Disney, fintech, healthcare, and the public sector.
              </p>
              <Link href="/principal">
                <span className="inline-block mt-4 text-blue-400 hover:text-blue-300 font-medium cursor-pointer">
                  Read the full background &rarr;
                </span>
              </Link>
            </div>
          </Reveal>

          <div className="mt-10 md:mt-14">
            <Reveal>
              <h3 className="text-xl md:text-2xl font-semibold mb-2 text-white">
                Recent fractional CTO engagements
              </h3>
              <p className="text-sm text-white/55 mb-6">Client names confidential</p>
            </Reveal>
            <Suspense fallback={null}>
              <RecentEngagements />
            </Suspense>
          </div>
        </div>
      </section>

      {/* How 7Sigma works */}
      <Reveal>
        <SevenSigmaDifference />
      </Reveal>

      {/* Engagement models */}
      <Suspense fallback={null}>
        <Reveal>
          <ServicesOverview />
        </Reveal>
      </Suspense>

      {/* Engagement lifecycle */}
      <Suspense fallback={null}>
        <Reveal>
          <EngagementFlow />
        </Reveal>
      </Suspense>

      {/* Industries */}
      <Suspense fallback={null}>
        <IndustriesSection />
      </Suspense>

      {/* Insights */}
      <Suspense fallback={null}>
        <Reveal>
          <LatestBlogPosts />
        </Reveal>
      </Suspense>

      {/* Newsletter */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <Suspense fallback={null}>
            <Reveal>
              <NewsletterSubscribe />
            </Reveal>
          </Suspense>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 md:py-28 relative isolate overflow-hidden">
        <Reveal className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-[1.75rem] md:text-5xl font-bold mb-6 text-white tracking-tight">
            Start a conversation
          </h2>
          <p className="text-base md:text-lg text-white/70 mb-8 md:mb-12">
            Tell 7Sigma what you are building and where it is stuck.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Lift>
              <Link href="/contact">
                <Button
                  size="lg"
                  className="button-primary py-5 px-7 text-base sm:py-6 sm:px-8 sm:text-lg"
                >
                  Book a meeting
                </Button>
              </Link>
            </Lift>
            <Lift>
              <Link href="/services">
                <Button
                  size="lg"
                  className="button-secondary py-5 px-7 text-base sm:py-6 sm:px-8 sm:text-lg"
                  variant="ghost"
                >
                  How 7Sigma helps
                </Button>
              </Link>
            </Lift>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
